import { LoggerService } from "@bfx/logger";

import { ActivatedRoute } from "@angular/router";
import {
  BenchmarkOrderType,
  BigNumber,
  CurrencyImpl,
  moment,
  OrderState,
  PositionUnit,
  Tenor,
  TradingGroupImpl,
} from "shared/cubiq-model-multi-asset";
import {
  BaseBMRiskModel,
  BLOTTER_RELOAD_TYPE,
  BmarkRiskOrderDataProvider,
  RiskBMOrderDataListener,
  RiskModelDataListener,
  RiskSubscriptionType,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { ConfiguredLogger } from "shared/cubiq-services/lookup/model/impl/bmrisk/ConfiguredLogger";
import { SpotRiskTGNet } from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskTGNet";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";
import { ValueDateHelper } from "shared/cubiq-services/model/orders/ValueDateHelper";
import { CcyLookupService } from "../CcyLookupService";
import { TraderGroupLookupService } from "../TraderGroupLookupService";

const logger = LoggerService.for(__dirname);
export const riskLogger = new ConfiguredLogger(logger);

const SPOTRISK_LOG_PREFIX = "SpotRisk";
const ONE_SPACE_PLEASE_CAROL = " ";

/**
 * Internal class used to manage all aggreagtions for a single value date
 */
class ValueDateTotal {
  public total: SpotRiskTGNet;

  constructor(
    public valueDate: number,
    public tenor: Tenor,
  ) {
    this.total = new SpotRiskTGNet(null, "");
  }
}

/**
 * External view-friendly class retrieved from getValueDateInfos() in SpotRiskModel that returns
 * a row for Value date
 */
export class ValueDateTotalInfo {
  constructor(
    public valueDate: number,
    public valueDateStr: string,
    public netAmount: BigNumber,
  ) {}
}

/**
 * Base Class of types that maintain trader group risks
 */
abstract class SpotRiskNet {
  public orderCount: number = 0;
  public tgTotals: Map<string, SpotRiskTGNet> = new Map<string, SpotRiskTGNet>();
  public desc: string;
  protected toStringIndent = ONE_SPACE_PLEASE_CAROL;

  constructor(public name: string) {
    this.desc = "[SpotRiskNet][" + name + "] %s";
  }

  private findOrCreateTraderGroupData(tg: TradingGroupImpl): SpotRiskTGNet {
    let tgId: string = tg?.groupId?.toString();
    let found: SpotRiskTGNet = this.findTraderGroupData(tg);
    if (!found) {
      riskLogger.debug(this.desc + " :  No baseline nets for TG" + tg.groupCode + ":" + tg.groupId);
      found = new SpotRiskTGNet(tg?.groupId, tg.groupCode);
      this.tgTotals.set(tgId, found);
    }
    return found;
  }

  private findTraderGroupData(tg: TradingGroupImpl): SpotRiskTGNet {
    let tgId: string = tg?.groupId?.toString();
    let found: SpotRiskTGNet = this.tgTotals.get(tgId);
    if (found) {
      return found;
    }
    return null;
  }

  public findTraderGroupDataByRisk(risk: BmarkRiskData): SpotRiskTGNet {
    let riskId: string = risk.riskId;
    return [...this.tgTotals.values()].find((spotRiskNet: SpotRiskTGNet) =>
      spotRiskNet.hasRisk(riskId),
    );
  }

  applyRisk(risk: BmarkRiskData, order: BmarkOrderData, tg: TradingGroupImpl): boolean {
    let ret: boolean = false;
    if (tg) {
      let tgData: SpotRiskTGNet = this.findOrCreateTraderGroupData(tg);
      if (tgData) {
        ret = tgData.applyRisk(risk, order);
        if (ret) {
          this.orderCount = this.orderCount + 1;
        } else {
          // This may be a new order or priming
          if (riskLogger.infoEnabled) {
            riskLogger.info(
              this.desc,
              "Error applying risxk to TG. so cannot increment order count" +
                tg.groupCode +
                ":" +
                tg.groupId,
            );
          }
        }
      } else {
        if (riskLogger.infoEnabled) {
          riskLogger.info(
            this.desc,
            "Cannnot apply risk as no TG found. so cannot increment order count" +
              tg.groupCode +
              ":" +
              tg.groupId,
          );
        }
      }
    } else {
      riskLogger.trace(this.desc, "Attempt to applyRisk with no traderGroup");
    }
    return ret;
  }

  removeRisk(risk: BmarkRiskData, order: BmarkOrderData, tg: TradingGroupImpl): boolean {
    let ret: boolean = false;
    if (tg) {
      let tgData: SpotRiskTGNet = this.findTraderGroupData(tg);
      if (tgData) {
        ret = tgData.removeRisk(risk, order);
        if (ret) {
          this.orderCount = this.orderCount - 1;
        } else {
          // All additions also do a remove so this should not be at warn level
          if (riskLogger.infoEnabled) {
            riskLogger.info(
              this.desc,
              "Error removing risk from trader group - totals may be off " +
                tg.groupCode +
                ":" +
                tg.groupId,
            );
          }
        }
      } else {
        if (riskLogger.infoEnabled) {
          riskLogger.info(
            this.desc,
            "Cannot find trader group for risk - totals may be off " +
              tg.groupCode +
              ":" +
              tg.groupId,
          );
        }
      }
    } else {
      riskLogger.trace(this.desc, "Attempt to removeRisk with no traderGroup");
    }
    return ret;
  }

  toString(): string {
    let _toString: string = "";
    for (const tgNet of this.tgTotals.values()) {
      _toString = _toString + this.toStringIndent + tgNet.toString() + "\n";
    }
    return _toString;
  }
}

/**
 * Class to describe the Net Fill Errors for a Set
 */
export class SpotRiskNetFillError extends SpotRiskNet {
  static FILL_ERROR_STATES: Array<OrderState> = [OrderState.FILL_ERROR, OrderState.FILL_EXPIRED];

  constructor() {
    super("FILL_ERROR");
    this.toStringIndent = "\t";
  }

  applyRisk(risk: BmarkRiskData, order: BmarkOrderData, tg: TradingGroupImpl): boolean {
    let ret: boolean = false;
    if (
      risk &&
      order &&
      order.orderState &&
      SpotRiskNetFillError.FILL_ERROR_STATES.includes(order.orderState)
    ) {
      ret = super.applyRisk(risk, null, tg);
    }
    return ret;
  }

  removeRisk(risk: BmarkRiskData, order: BmarkOrderData, tg: TradingGroupImpl): boolean {
    let ret: boolean = super.removeRisk(risk, null, tg);
    return ret;
  }

  public toString(): string {
    let _toString: string = "";
    if (this.orderCount > 0) {
      _toString = _toString + " [SpotRiskNetFillError [orderCount:" + this.orderCount + "]\n";
      _toString = _toString + super.toString();
      _toString = _toString + "\n ]";
    }
    return _toString;
  }
}

const TG_DBG_INTERNAL_TOTAL = "INT TOTAL";
const TG_DBG_EXTERNAL_TOTAL = "EXT TOTAL";

/**
 * Class to the Net Risk for a Pair.
 */
export class SpotRiskPair extends SpotRiskNet {
  public internalTotal: SpotRiskTGNet = new SpotRiskTGNet(null, TG_DBG_INTERNAL_TOTAL);
  public externalTotal: SpotRiskTGNet = new SpotRiskTGNet(null, TG_DBG_EXTERNAL_TOTAL);

  /** Map of value date key to aggregate for that date */
  private valueDateTotals: Map<string, ValueDateTotal> = new Map();

  public errors: SpotRiskNetFillError = new SpotRiskNetFillError();
  public unitType: PositionUnit;
  public filteredOut: boolean = false;

  /** Cached list of last created view models, if still valid otherwise null */
  private valueDateTotalInfos: ValueDateTotalInfo[] = null;

  public nonSpotAllocationCount: number = 0;

  private npvAvailable: { [key: string]: boolean } = {};

  constructor(
    public baseCCY: string,
    public nonBaseCCY: string,
    public baseCCYImpl: CurrencyImpl,
    public total: BigNumber = new BigNumber(0),
  ) {
    super(baseCCY + nonBaseCCY);
    this.unitType = baseCCYImpl != null ? baseCCYImpl.positionUnitId : null;
    this.desc =
      baseCCY +
      nonBaseCCY +
      "[" +
      (this.unitType != null ? PositionUnit[baseCCYImpl.positionUnitId] : "") +
      "] %s";
  }

  apply(risk: BmarkRiskData, order: BmarkOrderData, tg: TradingGroupImpl) {
    super.applyRisk(risk, order, tg);
    if (risk.isHedge === false) {
      this.npvAvailable[risk.riskId] = risk.useNPV;
    }
    this.errors.applyRisk(risk, order, tg);

    if (this.isInternalTg(tg)) {
      this.internalTotal.applyRisk(risk, order);
      this.applyInternalValueDate(risk, order);
    } else {
      this.externalTotal.applyRisk(risk, order);
    }

    this.updateTotal();
  }

  remove(risk: BmarkRiskData, order: BmarkOrderData, tg: TradingGroupImpl) {
    super.removeRisk(risk, order, tg);
    delete this.npvAvailable[risk.riskId];
    this.errors.removeRisk(risk, order, tg);
    if (this.isInternalTg(tg)) {
      this.internalTotal.removeRisk(risk, order);
      this.removeInternalValueDate(risk, order);
    } else {
      this.externalTotal.removeRisk(risk, order);
    }

    this.updateTotal();
  }

  public hasNpvWarning(): boolean {
    return Object.values(this.npvAvailable).findIndex((a) => a === false) >= 0;
  }

  updateTotal() {
    this.total = new BigNumber(0)
      .plus(this.internalTotal.netBaseAmt)
      .minus(this.externalTotal.netBaseAmt);
  }

  isInternalTg(tg: TradingGroupImpl) {
    // NB Need to get TraderGroupType Enum from server to the
    // 7 is BM-Internal; - all other ones are external
    return tg
      ? tg.typeId.equals(7)
      : // default to true if TG unavailable
        true;
  }

  /**
   * Add the allocations in the risk to their respective value date totals
   * @param risk
   * @param order
   */
  private applyInternalValueDate(risk: BmarkRiskData, order: BmarkOrderData) {
    const allocations = risk.allocations;
    allocations.forEach((allocation) => {
      const risk = new BmarkRiskData(order, [allocation]);
      const tenor = allocation.settleTenor ?? Tenor.EMPTY;
      const valueDate = allocation.settleDate?.valueOf() ?? 0;
      const key = `${tenor}-${valueDate}`;
      let vdAggregate = this.valueDateTotals.get(key);
      if (!vdAggregate) {
        vdAggregate = new ValueDateTotal(valueDate, tenor);
        this.valueDateTotals.set(key, vdAggregate);
      }
      let riskApplied: boolean = vdAggregate.total.applyRisk(risk, order);
      if (tenor !== Tenor.SPOT && riskApplied === true) {
        this.nonSpotAllocationCount++;
      }
    });

    this.valueDateTotalInfos = null;
  }

  /**
   * Remove the allocations in the risk from their respective value date totals
   * @param risk
   * @param order
   */
  private removeInternalValueDate(risk: BmarkRiskData, order: BmarkOrderData) {
    const allocations = risk.allocations;
    allocations.forEach((allocation) => {
      const risk = new BmarkRiskData(order, [allocation]);
      const tenor = allocation.settleTenor ?? Tenor.EMPTY;
      const valueDate = allocation.settleDate?.valueOf() ?? 0;
      const key = `${tenor}-${valueDate}`;
      let vdAggregate = this.valueDateTotals.get(key);
      if (vdAggregate) {
        let riskRemoved: boolean = vdAggregate.total.removeRisk(risk, order);
        if (tenor !== Tenor.SPOT && riskRemoved == true) {
          this.nonSpotAllocationCount--;
        }
        if (vdAggregate.total.getNumOrders() == 0) {
          this.valueDateTotals.delete(key);
        }
      }
    });
    this.valueDateTotalInfos = null;
  }

  /**
   * Create view-friendly models of the value date totals for this pair, with all the sorting
   * and formatting already done
   * @returns
   */
  private createValueDateTotalInfos(): ValueDateTotalInfo[] {
    const retInfos: ValueDateTotalInfo[] = [];
    const valueDateHelper = new ValueDateHelper(null);
    if (this.valueDateTotals?.size > 0) {
      const nets = [...this.valueDateTotals.values()].sort((a, b) => a.valueDate - b.valueDate);
      nets.forEach((valueDateTotal) => {
        const settleDate = moment(valueDateTotal.valueDate);
        let valueDateStr: string = valueDateHelper.formatBMRiskValueDate(
          settleDate,
          valueDateTotal.tenor,
        );
        const total = valueDateTotal.total;
        const info = new ValueDateTotalInfo(
          valueDateTotal.valueDate,
          valueDateStr,
          total.netBaseAmt,
        );

        retInfos.push(info);
      });
    }
    return retInfos;
  }

  /**
   * Get the view-level models for the Value Date aggregates - can be used to create tooltips,
   * titles, or small table (of value date, amount, intent)
   * @returns
   */
  public getValueDateTotalInfos(): ValueDateTotalInfo[] {
    if (!this.valueDateTotalInfos) {
      this.valueDateTotalInfos = this.createValueDateTotalInfos();
    }
    return this.valueDateTotalInfos;
  }

  /**
   * Does this pair have any non-spot allocations?
   * @returns
   */
  public hasFwdOrders(): boolean {
    return this.nonSpotAllocationCount > 0;
  }

  public toString(): string {
    let _toString: string =
      this.desc + // THis will have a %s in it but better than continuous string concats
      "[Total:" +
      this.total.toString() +
      "]" +
      "\n " +
      this.internalTotal.toString() +
      "\n " +
      this.externalTotal.toString();

    _toString = _toString + super.toString();

    _toString = _toString + "\n" + this.errors.toString();
    return _toString;
  }
}

class TypeIndex {
  public key: string;
  constructor(
    public risk: BmarkRiskData,
    public order: BmarkOrderData,
    public tg: TradingGroupImpl,
  ) {
    this.key = order.orderId + "::" + risk.riskId;
  }
}

/**
 * Class to define the structure of the data to be used in the SpotRisk Tab of the Benchmark Blotter
 * @see https://www.bcdfx.com/ijira/browse/OMS-24622
 *
 */
export class SpotRiskModel extends BaseBMRiskModel implements RiskBMOrderDataListener {
  subscribers: Set<RiskModelDataListener> = new Set<RiskModelDataListener>();
  public spotRiskData: Map<string, SpotRiskPair>;
  public typeMap: Map<RiskSubscriptionType, Map<string, TypeIndex>> = new Map();

  /**
   * Ensure we only connect once to all data services
   */
  private subscribedProviders: Set<BmarkRiskOrderDataProvider> = new Set();

  /**
   * Flag set to true when updating orders with an empty model - when true we can get away without do a remove before adding
   */
  private primingModel: boolean = false;

  constructor(
    protected clientOrderProv: BmarkRiskOrderDataProvider,
    protected traderGroupService: TraderGroupLookupService,
    protected ccyService: CcyLookupService,
    protected userEnabled: Promise<boolean>,
    protected cmdLine: ActivatedRoute,
  ) {
    super();
    riskLogger.initialiseLogging(cmdLine, "spotRiskLogging", SPOTRISK_LOG_PREFIX);
    if (null == traderGroupService) {
      riskLogger.error("No TraderGroupLookupService provided");
    }
    if (null == ccyService) {
      riskLogger.error("No CcyLookupService provided");
    }

    this.resetData();
    userEnabled.then((isEnabled) => {
      // We only want to respond to model changes if user can see the positions
      if (isEnabled) {
        this.primeAndMonitorClientData();
      } else {
        riskLogger.error("No ClientOrder BmarkRiskOrderDataProvider provided");
      }
    });
  }

  public subscribe(subscriber: RiskModelDataListener) {
    this.subscribers.add(subscriber);
  }

  public unSubscribe(subscriber: RiskModelDataListener) {
    this.subscribers.delete(subscriber);
  }

  informSubscribersOfReload(type: BLOTTER_RELOAD_TYPE) {
    riskLogger.debug(
      "Informing [" +
        this.subscribers.size +
        "] subscribers of Reload:" +
        BLOTTER_RELOAD_TYPE[type],
    );
    for (const subscriber of this.subscribers) {
      subscriber.onBlotterReload(type);
    }
  }

  informSubscribersOfUpdatedPairs(updatedPairs: Set<string>) {
    riskLogger.debug(
      "Informing [" + this.subscribers.size + "] subscribers of UpdatedPairs:" + updatedPairs,
    );
    for (const subscriber of this.subscribers) {
      subscriber.onUpdatePairs(updatedPairs);
    }
  }

  informSubscribersOfRemovedPairs(removedPairs: Set<string>) {
    riskLogger.debug(
      "Informing [" + this.subscribers.size + "] subscribers of RemovedPairs:" + removedPairs,
    );
    for (const subscriber of this.subscribers) {
      subscriber.onRemovePairs(removedPairs);
    }
  }

  /**
   * Called when the blotter is reloaded for any reason
   * @param reason
   * @param type
   */
  onBlotterReload(reason: BLOTTER_RELOAD_TYPE, type: RiskSubscriptionType) {
    try {
      if (reason) {
        riskLogger.info("+onBlotterReload " + reason);

        switch (reason) {
          case BLOTTER_RELOAD_TYPE.FILTER_CHANGE: {
            this.resetData(type);
            break;
          }
          case BLOTTER_RELOAD_TYPE.FIXING_CHANGE: {
            this.resetData(type);
            break;
          }
          case BLOTTER_RELOAD_TYPE.VIEW_CHANGE: {
            this.resetData(type);
            break;
          }
          default: {
            // NOP
          }
        }

        this.informSubscribersOfReload(reason);

        this.debugDump();
      }
    } catch (ex) {
      riskLogger.error("exception in onBlotterReload " + ex);
    }
  }
  /**
   * Called by a provider when an order is added or updated
   * @param update
   * @param type
   */
  onUpdateOrders(update: Set<BmarkOrderData>, type: RiskSubscriptionType) {
    this.primingModel = this.spotRiskData.size == 0;
    try {
      if (update != null && update.size > 0) {
        logger.out.info(
          `Updating Spot Risk Orders: count = ${update.size}, current SpotRiskMap size = ${this.spotRiskData.size}`,
        );

        let updatedPairs: Set<string> = new Set<string>();

        for (const order of update) {
          if (!this.shouldIgnoreOrderUpdate(order)) {
            riskLogger.debug("Updating order " + order.orderId);
            const risk = order.risk;
            let pair: SpotRiskPair = this.findOrCreatePairRiskInModelData(risk);
            let tg = this.traderGroupService.getTraderGroup(risk.deskId);
            let sourceTg: TradingGroupImpl = null;

            let sourceTgNet: SpotRiskTGNet = this.findTgTotalInModelData(risk, pair);
            if (sourceTgNet != null && !sourceTgNet.tgId.equals(risk.deskId)) {
              sourceTg = this.traderGroupService.getTraderGroup(sourceTgNet.tgId);
            }

            // as the various providers see an update as a "replace" (They are tables)
            // we need to remove the amount from the nets before adding it again.
            this.replace(type, pair, risk, order, tg, sourceTg);

            updatedPairs.add(pair.name);
          } else {
            riskLogger.debug("Ignoring Order  " + order);
          }
        }
        this.debugDump();

        this.informSubscribersOfUpdatedPairs(updatedPairs);
      }
    } catch (ex) {
      riskLogger.error("exception in onUpdateOrders " + ex);
    } finally {
      this.primingModel = false;
    }
  }

  replace(
    type: RiskSubscriptionType,
    pair: SpotRiskPair,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
    sourceTg: TradingGroupImpl = null,
  ) {
    if (!this.primingModel) {
      pair.remove(risk, order, sourceTg != null ? sourceTg : tg);
    }
    pair.apply(risk, order, tg);

    this.typeIndex(type, risk, order, tg);
  }

  typeIndex(
    type: RiskSubscriptionType,
    risk: BmarkRiskData,
    o: BmarkOrderData,
    t: TradingGroupImpl,
  ) {
    let incoming: TypeIndex = new TypeIndex(risk, o, t);
    let found: Map<string, TypeIndex> = this.typeMap.get(type);
    if (found) {
      found.delete(incoming.key);
      found.set(incoming.key, incoming);
    } else {
      found = new Map<string, TypeIndex>();
      this.typeMap.set(type, found);

      found.set(incoming.key, incoming);
    }
  }

  /**
   * called by a provider when na order is deleted
   * @param remove
   * @param type
   */
  onRemoveOrders(remove: Set<BmarkOrderData>, type: RiskSubscriptionType, filter: boolean = false) {
    try {
      if (remove && remove.size > 0) {
        riskLogger.info(
          "+onRemoveOrders " + remove.size + ":" + type + ":" + (filter ? "[filter]" : ""),
        );
        let updatedPairs: Set<string> = new Set<string>();

        for (const order of remove) {
          riskLogger.debug("Removing order " + order.orderId);
          let risk: BmarkRiskData = order.risk;
          let tg = this.traderGroupService.getTraderGroup(risk.deskId);
          let pair: SpotRiskPair = this.findPairRiskInModelData(risk);
          if (null != pair) {
            pair.remove(risk, order, tg);

            let found: Map<string, TypeIndex> = this.typeMap.get(type);
            if (found) {
              found.delete(new TypeIndex(risk, order, tg).key);
            }

            updatedPairs.add(pair.name);

            if (pair.externalTotal.getNumOrders() == 0 && pair.internalTotal.getNumOrders() == 0) {
              riskLogger.debug("Removing pair " + pair.name);
              this.spotRiskData.delete(pair.name);
            }
          }
        }

        if (updatedPairs.size > 0) {
          this.informSubscribersOfRemovedPairs(updatedPairs);
        }

        this.debugDump();
      }
    } catch (ex) {
      riskLogger.error("exception in onRemoveOrders " + ex);
    }
  }

  /**
   * Reset the data structures
   */
  resetData(type: RiskSubscriptionType = null) {
    logger.out.info(`Resetting all risk - current pair count = ${this.spotRiskData?.size ?? 0}`);
    riskLogger.debug("Resetting SpotRiskModel");
    this.spotRiskData = new Map<string, SpotRiskPair>();
    this.typeMap = new Map<RiskSubscriptionType, Map<string, TypeIndex>>();
  }

  /**
   * Prime the model
   */
  primeAndMonitorClientData() {
    this.primeAndMonitorProvider(this.clientOrderProv, RiskSubscriptionType.CLIENT);
  }

  primeAndMonitorHedgeData(hedgeOrdersProv: BmarkRiskOrderDataProvider) {
    this.primeAndMonitorProvider(hedgeOrdersProv, RiskSubscriptionType.HEDGE);
  }

  primeAndMonitorProvider(provider: BmarkRiskOrderDataProvider, type: RiskSubscriptionType) {
    if (provider) {
      this.primeOrderData(provider, type).then((orderData: Set<BmarkOrderData>) => {
        this.connectToOrderDataModel(provider, type);
        this.onUpdateOrders(orderData, type);
      });
    }
  }
  /**
   * We extract priming data from the ClientOrders agGridInstance
   * ..waiting for it, if it is not primed yet
   */
  private primeOrderData(
    provider: BmarkRiskOrderDataProvider,
    type: RiskSubscriptionType,
  ): Promise<Set<BmarkOrderData>> {
    return new Promise<Set<BmarkOrderData>>((res, rej) => {
      provider.primeData().then(
        (orders: Set<BmarkOrderData>) => {
          res(orders);
        },
        (ex: Error) => {
          rej(null);
        },
      );
    });
  }

  /**
   * We connect to the agGrid instance in the Client orders tab
   * of the Benchmark Blotter for updates to Client Orders
   */
  connectToOrderDataModel(dataModel: BmarkRiskOrderDataProvider, type: RiskSubscriptionType) {
    if (!this.subscribedProviders.has(dataModel)) {
      this.subscribedProviders.add(dataModel);
      dataModel.subscribe(type, this);
    }
  }

  findPairRiskInModelData(risk: BmarkRiskData): SpotRiskPair {
    let pair: string = risk.baseCcy + risk.nonBaseCcy;
    let pairRisk = this.spotRiskData.get(pair);
    return pairRisk;
  }

  findTgTotalInModelData(risk: BmarkRiskData, pair: SpotRiskPair): SpotRiskTGNet {
    return pair.findTraderGroupDataByRisk(risk);
  }

  findOrCreatePairRiskInModelData(risk: BmarkRiskData): SpotRiskPair {
    let pairRisk = this.findPairRiskInModelData(risk);
    if (null == pairRisk) {
      riskLogger.debug("No baseline risk for " + risk.baseCcy + risk.nonBaseCcy);
      let pair: string = risk.baseCcy + risk.nonBaseCcy;
      let ccyImpl: CurrencyImpl = this.ccyService.getCcy(risk.baseCcy);

      pairRisk = new SpotRiskPair(risk.baseCcy, risk.nonBaseCcy, ccyImpl);
      this.spotRiskData.set(pair, pairRisk);
    }
    return pairRisk;
  }

  shouldIgnoreOrderUpdate(order: BmarkOrderData) {
    return (
      null == order ||
      null == order.orderState ||
      (order?.fixingDetails?.bmOrderType !== BenchmarkOrderType.PositionOrder &&
        (OrderState.ACCEPTANCE_NEEDED === order?.orderState ||
          OrderState.EXT_ACCEPTANCE_NEEDED === order?.orderState))
    );
  }

  debugDump() {
    if (riskLogger.infoEnabled) {
      riskLogger.info(this.toString());
    }
  }

  public toString(): string {
    let _toString: string = "[SpotRiskModel] ";

    _toString = _toString + "\nRiskData";
    if (this.spotRiskData && this.spotRiskData.size > 0) {
      for (const value of this.spotRiskData.values()) {
        _toString = _toString + "\n " + value.toString();
      }
    } else {
      _toString = _toString + "\nEMPTY";
    }

    _toString = _toString + "\nTypeIndex";
    if (this.typeMap && this.typeMap.size > 0) {
      for (const typeKey of this.typeMap.keys()) {
        let typeValue: Map<string, TypeIndex> = this.typeMap.get(typeKey);
        _toString = _toString + "\n " + RiskSubscriptionType[typeKey];
        for (const indexKey of typeValue.keys()) {
          _toString = _toString + "\n  " + indexKey.toString();
        }
      }
    } else {
      _toString = _toString + "\nEMPTY";
    }
    return _toString;
  }
}
